import { json, type LoaderFunctionArgs } from '@remix-run/node'
import {
	NavLink,
	Outlet,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTrigger,
} from '#app/components/ui/drawer.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { cn, getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

const calculatePosition = (index: number) => {
	// Get the viewport width
	const vw = typeof window !== 'undefined' ? window.innerWidth : 1024

	// Adjust parameters based on screen size
	const a = vw < 768 ? 40 : 40 // Smaller base radius for mobile
	const b = vw < 768 ? 6 : 8 // Smaller space between rings for mobile
	const c = 0.5 // Angle increment remains the same

	// Parametric equations for spiral
	const angle = index * c
	const radius = a + b * angle
	const x = radius * Math.sin(angle)
	const y = radius * Math.cos(angle)

	return { x, y }
}

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const timeRange = url.searchParams.get('timeRange') || '24'
	const hours = parseInt(timeRange) + 6 // Always add 6 hours to the range

	const xHoursAgo = new Date(Date.now() - hours * 60 * 60 * 1000)

	const notes = await prisma.note.findMany({
		where: {
			createdAt: {
				gte: xHoursAgo,
			},
		},
		select: {
			id: true,
			title: true,
			owner: {
				select: {
					id: true,
					name: true,
					username: true,
					image: {
						select: {
							id: true,
						},
					},
				},
			},
		},
	})

	const formattedNotes = notes.map((note) => ({
		...note,
		owner: {
			...note.owner,
			imageId: note.owner.image?.id,
		},
	}))

	return json({ allNotes: formattedNotes, selectedTimeRange: timeRange })
}

export default function VandaagRoute() {
	const data = useLoaderData<typeof loader>()
	const [searchParams, setSearchParams] = useSearchParams()
	const user = useOptionalUser()
	const navLinkDefaultClassName =
		'line-clamp-2 block rounded-l-full py-2 pl-8 pr-6 text-base lg:text-xl'

	const handleTimeRangeChange = (range: string) => {
		setSearchParams((params) => {
			params.set('timeRange', range)
			return params
		})
	}

	return (
		<main className="container flex h-full min-h-[480px] px-0 pb-12 md:px-8">
			<div className="fixed left-4 top-1/2 z-50 -translate-y-1/2 md:left-8">
				<div className="relative h-[80px] w-9 rounded-full shadow-md">
					<input
						type="checkbox"
						className="peer absolute z-20 h-full w-full cursor-pointer opacity-0"
						checked={searchParams.get('timeRange') === '48'}
						onChange={(e) =>
							handleTimeRangeChange(e.target.checked ? '48' : '24')
						}
					/>
					<div className="absolute inset-0 rounded-full transition-colors duration-300" />
					<div className="absolute left-1 top-[10px] z-10 h-7 w-7 rounded-full bg-muted transition-transform duration-300 ease-in-out peer-checked:translate-y-[32px] peer-active:h-7">
						<span
							className="absolute left-1/2 top-[6px] -translate-x-1/2 whitespace-nowrap text-xs font-bold text-muted-foreground/90 transition-opacity duration-300"
							key={searchParams.get('timeRange')}
						>
							{searchParams.get('timeRange') === '48' ? '48' : '24'}
						</span>
					</div>
					<div className="flex h-full flex-col items-center justify-between py-4 text-xs font-bold">
						<span className="rotate-270 text-muted-foreground/40">24</span>
						<span className="rotate-270 text-muted-foreground/40">48</span>
					</div>
				</div>
			</div>

			<Drawer>
				<div>
					{user ? (
						<DrawerTrigger
							asChild
							className="absolute bottom-4 left-1/2 z-50 inline-flex w-full max-w-[90%] -translate-x-1/2 items-center justify-center rounded-full bg-primary px-6 py-4 text-sm font-medium text-primary-foreground outline-none ring-ring ring-offset-2 ring-offset-background transition-colors focus-within:ring-2 hover:bg-primary/80 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 sm:w-auto md:h-10"
						>
							<NavLink
								to={`nieuw?${searchParams.toString()}`}
								className={({ isActive }) =>
									cn(
										navLinkDefaultClassName,
										isActive && 'bg-accent text-button',
									)
								}
							>
								<Icon name="plus">Steek een lichtje aan</Icon>
							</NavLink>
						</DrawerTrigger>
					) : (
						<DrawerTrigger
							asChild
							className="absolute bottom-4 left-1/2 z-50 inline-flex w-full max-w-[90%] -translate-x-1/2 items-center justify-center rounded-full bg-primary px-6 py-4 text-sm font-medium text-primary-foreground outline-none ring-ring ring-offset-2 ring-offset-background transition-colors focus-within:ring-2 hover:bg-primary/80 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 sm:w-auto md:h-10"
						>
							<NavLink
								to="/login"
								className={({ isActive }) =>
									cn(
										navLinkDefaultClassName,
										isActive && 'bg-accent text-button',
									)
								}
							>
								Log in om een lichtje aan te steken
							</NavLink>
						</DrawerTrigger>
					)}
					<div className="vw-full absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
						{data.allNotes.map((note, noteIndex) => (
							<li
								key={note.id}
								className="absolute list-none p-1 pr-0"
								style={
									{
										'--x': `${calculatePosition(noteIndex).x}px`,
										'--y': `${calculatePosition(noteIndex).y}px`,
										transform: `translate(var(--x), var(--y))`,
										animation: `fadeIn 0.5s ease-in forwards`,
										opacity: 0,
										animationDelay: `${noteIndex * 300}ms`,
									} as React.CSSProperties
								}
							>
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<DrawerTrigger asChild>
												<NavLink
													to={`${note.id}?${searchParams.toString()}`}
													preventScrollReset
													prefetch="intent"
													className={({ isActive }) =>
														cn(navLinkDefaultClassName, isActive && 'bg-accent')
													}
												>
													<div className="relative h-[40px] w-[40px] opacity-100">
														<div className="absolute -top-5 left-1/2 h-[50px] w-7 -translate-x-1/2 drop-shadow-[0_0_10px_rgba(255,152,0,0.5)] filter">
															<svg
																viewBox="0 0 20 30"
																className="h-full w-full"
															>
																{/* Base flame (red) - bent right */}
																<path
																	d="M10 2 C15 8, 18 14, 18 20 C18 26, 13 28, 10 28 C7 28, 2 26, 2 20 C2 14, 5 8, 10 2"
																	fill="#dc1268"
																	className="animate-[flicker_2s_ease-in-out_infinite]"
																/>
																{/* Middle flame (orange) - bent left */}
																<path
																	d="M10 5 C8 10, 16 15, 16 20 C16 25, 12 27, 10 27 C8 27, 4 25, 4 20 C4 15, 12 10, 10 5"
																	fill="#f8a505"
																	className="animate-[flicker_2s_ease-in-out_infinite_0.2s]"
																/>
																{/* Inner flame (yellow) - centered */}
																<path
																	d="M10 8 C13 12, 14 16, 14 20 C14 24, 11.5 26, 10 26 C8.5 26, 6 24, 6 20 C6 16, 7 12, 10 8"
																	fill="#efe704"
																	className="animate-[flicker_2s_ease-in-out_infinite_0.4s]"
																/>
																{/* Core (white) - rounded */}
																<path
																	d="M10 14 C12 16, 12 18, 12 20 C12 22, 11 23, 10 23 C9 23, 8 22, 8 20 C8 18, 8 16, 10 14"
																	fill="white"
																	className="animate-[flicker_2s_ease-in-out_infinite_0.6s]"
																/>
															</svg>
														</div>
													</div>
												</NavLink>
											</DrawerTrigger>
										</TooltipTrigger>
										<TooltipContent>
											{note.owner.name ?? note.owner.username}
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</li>
						))}
					</div>
				</div>

				<div>
					<DrawerContent className="__className_212e56 fixed inset-x-4 bottom-0 mx-auto mb-4 min-h-[480px] max-w-[361px] overflow-hidden rounded-[36px] bg-[#FEFFFE] outline-none md:mx-auto md:w-full">
						<DrawerHeader>
							<DrawerClose className="mt-0 flex justify-end">
								<button
									className="focus-visible:shadow-focus-ring-button top right absolute z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#F7F8F9] text-[#949595] transition-transform focus:scale-95 active:scale-75"
									type="button"
								>
									<svg
										width="12"
										height="12"
										viewBox="0 0 12 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M10.4854 1.99998L2.00007 10.4853"
											stroke="#999999"
											stroke-width="3"
											stroke-linecap="round"
											stroke-linejoin="round"
										></path>
										<path
											d="M10.4854 10.4844L2.00007 1.99908"
											stroke="#999999"
											stroke-width="3"
											stroke-linecap="round"
											stroke-linejoin="round"
										></path>
									</svg>
								</button>
							</DrawerClose>
							<Outlet />
						</DrawerHeader>
					</DrawerContent>
					<DrawerTrigger></DrawerTrigger>
				</div>
			</Drawer>
		</main>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
